import { useMemo, useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import Accordion from 'react-bootstrap/Accordion';
import DOMPurify from 'dompurify';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  Col,
  Form,
  Badge,
  Spinner,
  ProgressBar
} from 'react-bootstrap';
import axios from "axios";
import {generatePdfThumb, generateVideoThumb, toggleBodyLoader} from './../../helper/custom-function';
import {SetMetaData} from './../../helper/page-title';
import ValidationHelper from "../../helper/validation";
import useRowSelection from "../../hooks/row-selection";
import useInfiniteList from "../../hooks/infinite-list";
import useColumnSettings from "../../hooks/column-setting";
import InfiniteListWrapper from "../../components/wrapper/infinite-list";
import { error_api_call, post_api_call, get_api_call, post_api_call_main} from "../../actions/action-creators/common";
import { alertResponseMessage } from "../../actions/action-creators/response";
import {LoaderMedium} from './../../components/loader';
import {filterTableData,
        masterTableFields,
        newResDefaultValues,
        fieldUpdateActiveValues,
        fieldUpdateStatus,
        fieldValidationStatus,
        fieldResourceEdit,
        fieldValidationResEdit
} from "../../data/formData/glosco/resource";
import {getProgramResourceConfig} from "../../data/formData/glosco/resourceProgramConfig";
import {SettingDropdownUi, TableCheckboxUi, RowDataUi} from "../../components/form/master-table/ui-static";
import FilterInput from "../../components/form/filter/filter-input";
import MasterForm  from '../../components/form/master';
import normalizeFilters from "../../helper/filter/normalize-filter";
import FilterFloatMaster from "../../components/form/filter/filter-float";
import {
  activeStatusOpp,
  activeStatusUpdateOpp
} from "../../data/formData/option";
import Fancybox from '../../components/plugin/fancyox';
import { handleDownload } from "../../components/button-action/download";
import ModalMaster from "../../modal/master";

const editResDefaultVal = {
    id: '',
    title: '',
    description: '',
    introduction: '',
    display_order: ''
}

function GloscoResourcesManage(props) {
    SetMetaData("Manage GLOSCO Resources", "", "");
    const [newResourceData, setNewResourceData] = useState({...newResDefaultValues});
    const [formStatusData, setStatusFormData] = useState({...fieldUpdateActiveValues});
    const [filterData, setFilterData] = useState({});
    const [tmpFilterData, setTmpFilterData] = useState({});
    const [errorData, setErrorData] = useState({resource_new: [], resource_status: [], resource_edit: []});
    const [isProcess, setIsProcess] = useState({resource_new: false, resource_status: false, filter: false, resource_edit: false});
    const [refreshKey, setRefreshKey] = useState(0);
    const [modalToggle, setModalToggle] = useState({resource_new : false, resource_edit: false});
    const [deletedResourceIds, setDeletedResourceIds] = useState([]);
    // const [modalLoader, setModalLoader] = useState({resource_edit: false});
    const [formEditData, setEditFormData] = useState({});
    const [masterFieldData, setMasterFieldData] = useState([]);

    const initialColumnSettings = {
        ...Object.fromEntries(masterTableFields.map(f => [f.fetch_key, true])),
        // session_id: true, // Manually add if not in the list
    };

    const { columnSettings, updateColumn } = useColumnSettings(initialColumnSettings);

    const [dropdownOpt, setDropdownOpt] = useState({
        active_status_id: activeStatusOpp,
        program_id: [],
        level_id: [],
        session_id: [],
        esg_level_id: [],
        esg_session_id: [],
        file_type: []
    });

    const normalizedFilter = useMemo(
    () =>
        normalizeFilters(filterData, {
            program_id      : "id",
            active_status_id: "id",
            session_id      : "id",
            level_id        : "id",
            file_type       : "id"
        }),
        [filterData]
    );

    const memoizedPayload = useMemo(() => ({
        fetch_type: "manage_resources_sa",
        filter: normalizedFilter
    }), [normalizedFilter]); // Only changes when filters change

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useInfiniteList({
        queryKey: ["manage_resources_sa", refreshKey],
        apiCall: payload => {
            return post_api_call("glosco-resources/list", payload)
        },
        limit: 100,
        // extraPayload: {
        //     fetch_type: "manage_resources_sa",
        //     filter: normalizedFilter
        // }
        extraPayload: memoizedPayload, // Use the memoized version
    });

    // const masterTableData = data?.pages.flatMap(page => page.data) ?? [];
    const masterTableData = Array.isArray(data?.pages)
                            ? data.pages.flatMap(page => Array.isArray(page?.data) ? page.data : [])
                            : [];
    // const visibleUserIds = masterTableData.map(r => r.id);
    const {
        // selectedIds,
        isChecked,
        isAllSelected,
        isIndeterminate,
        toggleRow,
        selectAllVisible,
        clearAllVisible
    } = useRowSelection({
        data: masterTableData,
        rowKey: "id",
        // value: formYearData.ref_ids_array || [],   // ✅ VERY IMPORTANT
        value: (formStatusData.ref_ids_array || []).map(String),
        maxSelection: 300,
        onLimitExceed: limit => {
            alertResponseMessage({
                alert_type: 3,
                message: `You can select maximum ${limit} users at once.`
            });
        },
        onChange: ids => {
            setStatusFormData(prev => ({
                ...prev,
                ref_ids_array: ids,
                ref_ids: ids.join(",")
            }));
        }
    });

    //Get Data from APIs
    const fetchSelectList = async(type, url) => {
      try{
          const selectApiData = await get_api_call(url, {});
          var data = [];
          if(selectApiData.status === 200){
              data = selectApiData.data;
              switch (type) {
                  case 'manage_resources_sa':
                    setDropdownOpt({...dropdownOpt,
                        program_id: data?.ifspd_programs,
                        level_id: data?.glosco_years,
                        session_id: data?.glosco_session,
                        esg_level_id: data?.esg_years,
                        esg_session_id: data?.esg_session
                    });
                    break;
                  default:
              }
          }

          return {
              data
          };
      }catch(error){
        error_api_call({error: `admin|screens|glosco|resource-manage|fetchSelectList|${error}`});
        return {data: []};
      }
    }

    useEffect(() => {
        fetchSelectList('manage_resources_sa', `dropdown-options/ifspd_programs,glosco_session,glosco_years,esg_session,esg_years?fetch_type=resources`);
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    const updateInput = (form_type, attrName, value, index=0) => {
        try {
            if(form_type === "resource_new") {
                if(errorData[form_type].length){
                    setErrorData({...errorData, [form_type]: []});
                }

                if(attrName === "title"
                    || attrName === "description"
                    || attrName === "introduction"
                    || attrName === "display_order"
                ){
                    let fields = [...newResourceData.files];
                    fields[index][attrName] = value;

                    setNewResourceData({...newResourceData, files: fields});
                }else{
                    let fields = {...newResourceData};
                    fields[attrName] = value;

                    setNewResourceData({...fields});
                }
                
            }else{
                
                switch (form_type) {
                    case 'filter':
                        let fieldsFilt = {...tmpFilterData};
                        fieldsFilt[attrName] = value;
                        setTmpFilterData({...fieldsFilt});
                        break;
                    case 'resource_status':
                        let fields = {...formStatusData};
                        fields[attrName] = value;
                        setStatusFormData({...fields});
                        break;
                    case 'resource_edit':
                        let fieldsEdit = {...formEditData};
                        fieldsEdit[attrName] = value;
                        setEditFormData({...fieldsEdit});
                        break;
                    default: 
                        break;
                }
            }
        } catch (error) {
            console.error(error);
            error_api_call({
                error: `admin|screens|glosco|resource-manage|updateInput|${error}`
            });
        }
    };
    //Toggle Modal
    const toggleModal = async(e, modal_type, toggleState, mod_data={}) => {
        try{
            if(toggleState){
                e.preventDefault();
            }
            if(toggleState){
                switch (modal_type) {
                    case 'resource_new':
                        setNewResourceData({...newResDefaultValues});
                        break;
                    case 'resource_edit':
                        setEditFormData({...editResDefaultVal, ...mod_data});
                        setMasterFieldData([{
                          form_type: 'resource_edit',
                          label: 'Details',
                          fields: [...fieldResourceEdit]
                        }]);
                        break;
                    default: 
                        break;
                }
                setErrorData({...errorData, [modal_type]: []});
            }

            setModalToggle({...modalToggle, [modal_type]: toggleState});
            
        }catch(error){
            console.error(error)
        }
    }
    //Submit Details
    const submitForm = async(e, form_type='') => {
        try{
            e.preventDefault();
            setErrorData({...errorData, [form_type]: []});
            setIsProcess({...isProcess, [form_type]: true});
            var errorDetails = [];
            var apiFormData = new FormData();
            //Validation
            var validation = [];
            var inputData = {};
            var validationFields = {};
            var api = '';
                
            switch (form_type) {
                case 'resource_new':
                    inputData = {...newResourceData};

                    if(!inputData.program_id){
                        validation.push("IFSPD Program is required");
                    }else if(getProgramResourceConfig(inputData.program_id.id).showLevel){
                        if(!inputData.level_id){
                            validation.push("Level/Year is required");
                        }
                    }
                    
                    if(!inputData.session_id){
                        validation.push("Council is required");
                    }

                    if(inputData.files.length === 0){
                        validation.push("Resources required");
                    }

                    break;
                case 'resource_status':
                    inputData = {...formStatusData};
                    validationFields = fieldValidationStatus;
                    break;
                case 'resource_edit':
                    inputData   = {...formEditData};
                    validationFields = fieldValidationResEdit;
                    break;
                default:
            }

            if(form_type !== 'resource_new'){
                validation = ValidationHelper.validation(inputData, validationFields).error;
            }

            if(validation.length){
                errorDetails = validation;
            }
          
            if(errorDetails.length > 0){
                setErrorData({...errorData, [form_type]: errorDetails});
                setIsProcess({...isProcess, [form_type]: false});
            }else{
                toggleBodyLoader('load');
                //Send Data to Server
                switch (form_type) {
                    case 'resource_status':
                        api = 'glosco-resources/update/status';
                        apiFormData.append('active_status_id', inputData.active_status_id.id);

                        const uniqueRefIds = [...new Set(inputData.ref_ids_array)];
                        uniqueRefIds.forEach(id => {
                            if (id !== "" && id !== null) {
                                apiFormData.append("ref_ids[]", Number(id));
                            }
                        });
                        break;
                    case 'resource_edit':
                        console.log(inputData, 'inputDatainputDatainputData')
                        api = 'glosco-resources/update/edit-resource';
                        apiFormData.append('id', inputData?.id);
                        apiFormData.append('title', inputData?.title);
                        apiFormData.append('description', inputData?.description ?? '');
                        apiFormData.append('introduction', inputData?.introduction ?? '');
                        apiFormData.append('display_order', inputData?.display_order ?? '');
                        break;
                    default:
                }

                const updateOppor = await post_api_call(`${api}`, apiFormData);
                if(updateOppor.status === 200){
                    alertResponseMessage({alert_type: 1, message: "Information Updated Successfully"});
                    // setBtnDisabled({...isBtnDisabled, [form_type]: true});

                    switch (form_type) {
                        case 'resource_status':
                            setStatusFormData({...fieldUpdateActiveValues});
                            break;
                        case 'resource_edit':
                            toggleModal('', 'resource_edit', false);
                            // setEditFormData({...editResDefaultVal});
                            break;
                        default:
                    }

                    setRefreshKey(prev => prev + 1); //RESET TABLE
                }else{
                    if(typeof updateOppor.message !== "undefined"){
                        setErrorData({...errorData, [form_type]: [updateOppor.message]});
                    }
                }
                setIsProcess({...isProcess, [form_type]: false});
                toggleBodyLoader('remove');
            }
          
        }catch(error){
            console.error(error);
            toggleBodyLoader('remove');
            setErrorData({...errorData, [form_type]: ['Something Went Wrong, Please Try After Some Time']});
            setIsProcess({...isProcess, [form_type]: false});
            error_api_call({error: `admin|screens|glosco|resource-manage|submitForm|${error}`});
        }
    }
    //Uploaded resources preview
    const previewResources = async(event) => {
        try {
            const files = event.target.files;
            if (!files.length) return;

            let uploadedFiles = [...newResourceData.files];
            let uploadedFileSize = newResourceData.file_size || 0;

            const allowedExt = ["png", "jpg", "jpeg", "pdf", "pptx", "ppt", "mp4"];

            const docType = {
                png: "image",
                jpg: "image",
                jpeg: "image",
                pdf: "pdf",
                pptx: "ppt",
                ppt: "ppt",
                mp4: "video"
            };

            const allowedSize = 300 * 1024 * 1024; // 300MB
            const allowedQty = 12;

            let isFileAdded = false;

            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                const ext = file.name.split('.').pop().toLowerCase();
                const type = docType[ext];

                // validations
                if (!allowedExt.includes(ext)) {
                    alertResponseMessage({alert_type: 2,
                                        message: `${ext}-Invalid file type.}`});
                    continue;
                }

                if (uploadedFiles.length >= allowedQty) {
                    alertResponseMessage({alert_type: 2,
                                        message: `Max 12 files allowed.`});
                    break;
                }

                if (uploadedFileSize + file.size > allowedSize) {
                    alertResponseMessage({alert_type: 2,
                                        message: `Total size exceeded (300MB).`});
                    break;
                }

                //Generate Thumbnails based on type
                let thumbnail = URL.createObjectURL(file); // Default for images
                
                if (type === 'video') {
                    thumbnail = await generateVideoThumb(file);
                } else if (type === 'pdf') {
                    thumbnail = await generatePdfThumb(file);
                } else if (type === 'ppt') {
                    thumbnail = "/assets/images/ppt-icon.png"; // Static icon
                }

                const resData = {
                    id: Date.now() + Math.random(),
                    data: file,
                    src: URL.createObjectURL(file),
                    thumbnail: thumbnail,
                    name: file.name,
                    title: '',
                    description: '',
                    introduction: '',
                    ext,
                    docType: type,
                    size: file.size,
                    progress: 0,
                    status: 'pending',
                    display_order: 1000
                };

                uploadedFiles.push(resData);
                uploadedFileSize += file.size;
                isFileAdded = true;
            }

            if (isFileAdded) {
                setNewResourceData({
                    ...newResourceData,
                    files: uploadedFiles,
                    file_size: uploadedFileSize
                });
            }

        } catch (error) {
            error_api_call({error: `admin|screens|glosco|resource-manage|previewResources|${error}`});
        }
    };      
    //Remove Resource
    const removeResource = (event, index) => {
        toggleBodyLoader('load');
        try {
            event.preventDefault();
            let files = [...newResourceData.files];

            // remove preview memory (important)
            if(files[index]?.src){
                URL.revokeObjectURL(files[index].src);
            }

            files.splice(index, 1);

            // recalculate total size
            const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);

            setNewResourceData({
                ...newResourceData,
                files,
                file_size: totalSize
            });
            toggleBodyLoader('remove');
        }catch (error) {
            toggleBodyLoader('remove');
            error_api_call({error: `admin|screens|glosco|resource-manage|removeResource|${error}`});
        }
    };
    //Upload Resources
    const uploadAllResources = async (event) => {
        event.preventDefault();
        toggleBodyLoader('load');
        try {
            setErrorData({ ...errorData, resource_new: [] });
            setIsProcess({ ...isProcess, resource_new: true });

            // 1. Validation
            var errorDetails = [];
            const inputData = { ...newResourceData };

            if (!inputData.program_id) {
                errorDetails.push("IFSPD Program required");
            } else if (getProgramResourceConfig(inputData.program_id.id).showLevel) {
                if (!inputData.level_id) errorDetails.push("Level/Year required");
            }

            if (!inputData.session_id) errorDetails.push("Council required");

            if (inputData.files.length === 0) {
                errorDetails.push("Resources required");
            }else{
                inputData.files.forEach((file, f_idx) => {
                    if(file.title.trim() === ''
                        || file.title.length > 200
                    ){
                        errorDetails.push(`Resource ${f_idx + 1}: Title required (Character Limit: 200)`);
                    }

                    if(file.description
                        && file.description.length > 2000
                    ){
                        errorDetails.push(`Resource ${f_idx + 1}: Description must be within 2000 Characters`);
                    }

                     if(file.introduction
                        && file.introduction.length > 1000
                    ){
                        errorDetails.push(`Resource ${f_idx + 1}: Introduction must be within 1000 Characters`);
                    }
                });
            }

            if (errorDetails.length > 0) {
                setErrorData({ ...errorData, resource_new: errorDetails });
                setIsProcess({ ...isProcess, resource_new: false });
                toggleBodyLoader('remove');
                return;
            }

            var uploadCount = 0;

            // 2. Loop through files
            for (const fileObj of inputData.files) {
                
                // SKIP files that were already successfully synced in a previous attempt
                if (fileObj.status === 'completed') continue;

                try {
                    // Set status to uploading
                    updateFileStatus(fileObj.id, 'uploading', 0);
                    // A. Get Presigned URL
                    const r2ApiCall = await post_api_call_main(`r2/get-presigned-url`, {
                        file_name: fileObj.name,
                        file_type: fileObj.ext,
                        fetch_key: getProgramResourceConfig(inputData.program_id.id).fetchKey,
                        file_size: fileObj.size/1024
                    });

                    if (r2ApiCall.status === 200) {
                        // B. Upload to R2
                        await axios.put(r2ApiCall.upload_url, fileObj.data, { // Send ONLY fileObj.data here
                            headers: { 
                                'Content-Type': fileObj.data.type || 'application/octet-stream' 
                            },
                            onUploadProgress: (progressEvent) => {
                                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                updateFileStatus(fileObj.id, 'uploading', percent);
                            },
                        });

                        //Thumbnail
                        if (fileObj.docType === 'video' || fileObj.docType === 'pdf') {
                            const responseThumb = await fetch(fileObj.thumbnail);
                            const thumbBlob = await responseThumb.blob();

                            await axios.put(r2ApiCall.thumb_url, thumbBlob, {
                                headers: { 'Content-Type': 'image/jpeg' }
                            });
                        }

                        // C. Sync to Laravel
                        const syncResponse = await post_api_call(`glosco-resources/sync`, {
                            program_id: inputData.program_id.id,
                            level_id: inputData.level_id?.id || null,
                            // session_id: inputData.session_id?.id || null,
                            session_id: typeof inputData.session_id?.id !== "undefined" ? inputData.session_id?.id : null,
                            uploaded_files: [{
                                title      : fileObj.title || fileObj.name,
                                introduction: fileObj.introduction,
                                description: fileObj.description,
                                r2_key     : r2ApiCall.file_key,
                                file_type  : fileObj.docType,
                                file_size  : fileObj.size/1024,//KB
                                file_name  : r2ApiCall.file_name,
                                display_order: fileObj.display_order
                            }]
                        });

                        if(syncResponse.status === 200) {
                            // Mark specifically as completed
                            updateFileStatus(fileObj.id, 'completed', 100);
                            uploadCount += 1;
                        } else {
                            throw new Error("DB Sync Failed");
                        }
                    }else{
                        updateFileStatus(fileObj.id, 'failed', fileObj.progress);
                    }
                } catch (fileError) {
                    // If one file fails, mark it but DON'T stop the whole loop
                    console.error(`Error uploading ${fileObj.name}:`, fileError);
                    updateFileStatus(fileObj.id, 'failed', fileObj.progress);
                    error_api_call({error: `admin|screens|glosco|uploadAllResources|fileError|${fileError}`}); 
                }
            }

            if(uploadCount === inputData.files.length){
                setModalToggle({...modalToggle, resource_new: false});
                alertResponseMessage({alert_type: 1, message: "Files Uploaded Successfully"});
                setRefreshKey(prev => prev + 1); //RESET TABLE
            }

            setIsProcess({ ...isProcess, resource_new: false });
            toggleBodyLoader('remove');

        } catch (error) {
            toggleBodyLoader('remove');
            setIsProcess({ ...isProcess, resource_new: false });
            error_api_call({error: `admin|screens|glosco|uploadAllResources|${error}`});        }
    };
    /**
     * Enhanced Helper to update status, progress, and UI state
     */
    const updateFileStatus = (fileId, status, progress) => {
        setNewResourceData(prev => ({
            ...prev,
            files: prev.files.map(f => 
                f.id === fileId ? { ...f, status, progress } : f
            )
        }));
    };
    //Retry failed uploads
    const retryFailedUploads = async (event) => {
        event.preventDefault();
        
        // 1. Identify which files actually failed
        const failedFiles = newResourceData.files.filter(file => file.status === 'failed');

        if (failedFiles.length === 0) {
            // Optional: Show a toast or message saying "No failed uploads to retry"
            return;
        }

        // 2. Reset the error data for a fresh attempt
        setErrorData({ ...errorData, resource_new: [] });

        // 3. Call your existing master function
        // Your existing 'uploadAllResources' already skips 'completed' files, 
        // so it will naturally only pick up the 'failed' ones.
        await uploadAllResources(event);
    };

    const totalUploadedFiles = newResourceData.files.length;
    const completedFiles = newResourceData.files.filter(f => f.status === 'completed').length;
    const pendingFilesCount = newResourceData.files.filter(f => f.status === 'pending').length;
    // const combinedProgress = totalUploadedFiles > 0 
    //     ? Math.round(newResourceData.files.reduce((acc, file) => acc + (file.progress || 0), 0) / totalUploadedFiles)
    //     : 0;
    const combinedProgress = totalUploadedFiles > 0 
        ? Math.round(newResourceData.files.reduce((acc, file) => {
            // Logic: Failed files don't contribute to the overall bar
            const progressValue = file.status === 'failed' ? 0 : (file.progress || 0);
            return acc + progressValue;
        }, 0) / totalUploadedFiles)
        : 0;

    const isUploadingAny = newResourceData.files.some(f => f.status === 'uploading');

    //Delete Resource
    const deleteResource = async(e, id) => {
        e.preventDefault();
        try{
            // status_change
            if (window.confirm(`Are you sure you want to delete this resource?`)){
                toggleBodyLoader('load');
                const deleteApiData = await post_api_call(`glosco-resources/update/delete`, {id});
                if(deleteApiData.status === 200){
                    setDeletedResourceIds(prev => ([
                        ...prev,
                        id
                    ]));
                    // setRefreshKey(prev => prev + 1); //RESET TABLE
                    alertResponseMessage({alert_type: 1, message: `Resource deleted Successfully!`});
                }

                toggleBodyLoader('remove');
            }
        }catch(error){
            toggleBodyLoader('remove');
            error_api_call({error: `admin|glosco|resource-manage|deleteResource|${error}`});
        }
    }

    return (
        <>
        <div className="row">
            <div className="col-lg-12 text-end">
                <div className="d-grid float-end">
                <button className="btn btn-info mb-2 p-2" onClick={(e) => toggleModal(e, 'resource_new', true)}>Add Resources</button>
                </div>
            </div>
            {/* Activate/Deactivate Res */}
            <div className="col-lg-12">
                <div className="card p-2 mb-2 mod-custom z-index-99999">
                    <Accordion
                        className="filter-custom acc-sm-header"
                    >
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                Activate/Deactivate
                            </Accordion.Header>
                            <Accordion.Body className="p-2">
                                <MasterForm
                                    formType={'resource_status'}
                                    data={formStatusData}
                                    _errorData={{...errorData}}
                                    _isProcess={{...isProcess}}
                                    // _isBtnDisabled={{...isBtnDisabled}}
                                    _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(form_type, field, value)}
                                    _submitForm={(e, form_type) => submitForm(e, form_type)}
                                    _formData={[{form_type: 'resource_status',
                                                label: "Status",
                                                fields: [...fieldUpdateStatus]}
                                            ]}
                                    selectOptions={{active_status_id:{
                                                        value: 'id',
                                                        label: 'status',
                                                        data: [...activeStatusUpdateOpp],
                                                    }}
                                                }
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>
            </div>
            {/* Filter Res  */}
            <div className="col-lg-12">
                <div className="card p-2 mb-2">
                    {/* Filter by: */}
                    <FilterFloatMaster
                        title="Filter Resources"
                        formType="filter"
                        data={tmpFilterData}
                        _isProcess={{...isProcess}}
                        _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(form_type, field, value)}
                        _formData={[{label: "Filter Resources:", display_type: 'col', fields: [...filterTableData]}]}
                        _submitForm = {(e, form_type) => {e.preventDefault(); setFilterData({...tmpFilterData})}}
                        _is_download={false}
                        userRoleIds={props.userRoleIds}
                        selectOptions={{
                                    level_id: {value: 'id',
                                                label: 'label',
                                                data: [...dropdownOpt.level_id]},
                                    program_id: {value: 'id',
                                                label: 'label',
                                                data: [...dropdownOpt.program_id]},
                                    session_id:  {value: 'id',
                                                label: 'label',
                                                data: [...dropdownOpt.session_id]},
                                    file_type:  {value: 'id',
                                                label: 'file_type',
                                                data: [...props.fileTypeOpt]},
                                    active_status_id:{value: 'id',
                                                    label: 'status',
                                                    data: activeStatusOpp}
                                }}
                        _resetFilterForm = {(e) => {e.preventDefault(); setTmpFilterData({})}}
                    />
                </div>
            </div>
            {/* Listing Res */}
            <InfiniteListWrapper
                dataLength={masterTableData.length}
                isLoading={isLoading}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                emptyMessage="No Record found!"
            >
                <div className="col-lg-12 grid-margin stretch-card">
                    <div className="card">
                        {/* SETTINGS */}
                        <SettingDropdownUi
                            checkedData={columnSettings}
                            fields={masterTableFields}
                            updateColumn= {(field, value) => updateColumn(field, value)}
                        />

                        {/* TABLE */}
                        <div className="card-body table-responsive sticky-table pt-0 pe-0 ps-3">
                            <table className="table">
                                <thead>
                                <tr>
                                    <th>
                                        <TableCheckboxUi
                                            id="select_row"
                                            name="select_row"
                                            checked={isAllSelected}
                                            ref={el => el && (el.indeterminate = isIndeterminate)}
                                            // onChange={(e) =>
                                            //     e.target.checked ? selectAllVisible() : clearAllVisible()
                                            // }
                                            onChange={(e) => e.target.checked
                                                            ? selectAllVisible()
                                                            : clearAllVisible()}
                                        />
                                    </th>

                                    {masterTableFields.map((col, i) => (
                                    (typeof col.accessRoleIds === "undefined" ||
                                        props.userRoleIds.some(role =>
                                        col.accessRoleIds.includes(role)
                                        )) &&
                                        columnSettings[col.fetch_key] && (
                                            <th key={i} className={`w-200p ${col.class_name || ""}`}>
                                                {col.title}
                                            </th>
                                        )
                                    ))}
                                    <th>Action</th>
                                </tr>
                                </thead>

                                <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="20" className="text-center p-4">
                                            <img
                                                alt="Loader"
                                                src={require("../../assets/images/Image-Loader.gif")}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    masterTableData.length > 0 
                                    ? (
                                        masterTableData.map((row, index) => 
                                            !deletedResourceIds.includes(row.id) && (
                                                <tr
                                                    key={index}
                                                    className={isChecked(row.id) ? "table-success" : ""}
                                                    onClick={() => toggleRow(row.id)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <td onClick={e => e.stopPropagation()}>
                                                        <TableCheckboxUi
                                                            id={row.id}
                                                            name="select_row"
                                                            checked={isChecked(row.id)}
                                                            onChange={() => toggleRow(row.id)}
                                                        />
                                                    </td>

                                                    {masterTableFields.map((tbl, i) => (
                                                        (typeof tbl.accessRoleIds === "undefined" ||
                                                        props.userRoleIds.some(role =>
                                                            tbl.accessRoleIds.includes(role)
                                                        )) &&
                                                        columnSettings[tbl.fetch_key] && (
                                                        <td key={i}>
                                                            {tbl.fetch_key === 'session_id' ? (
                                                                <span>{row.session_id === 0 ? 'Introduction' : `Council ${row.session_id}`}</span>
                                                            )
                                                            : (tbl.fetch_key === 'description' || tbl.fetch_key === 'introduction') ? (
                                                                row[tbl.fetch_key] && (
                                                                <div
                                                                    className="three-line"
                                                                    dangerouslySetInnerHTML={{ 
                                                                        __html: DOMPurify.sanitize(row[tbl.fetch_key], { ADD_TAGS: ["style"] }) 
                                                                    }}
                                                                />)
                                                            )   : 
                                                                <RowDataUi
                                                                    fetch_key={tbl.fetch_key}
                                                                    data={row}
                                                                />
                                                            }
                                                        </td>
                                                        )
                                                    ))}

                                                    <td onClick={e => e.stopPropagation()}>
                                                        <div className="d-flex">
                                                        {row.view_url
                                                        && (
                                                            <div>
                                                            <Fancybox
                                                                options={{
                                                                    Carousel: {
                                                                        infinite: false,
                                                                    },
                                                                    Toolbar: {
                                                                        display: {
                                                                            // right: ["fullscreen", "zoom", "download", "close"],//slideshow
                                                                            right: ["fullscreen", "zoom", "close"],//slideshow
                                                                        },
                                                                    },
                                                                }}
                                                                className="fancy-box"
                                                            >
                                                                {/* <a
                                                                    data-fancybox="gallery"
                                                                    data-download-src={row.download_url}
                                                                    href={row.view_url}
                                                                    className="fancybox image gallery-item m-0"
                                                                    rel="gallery-0"
                                                                    title="View"
                                                                > */}
                                                                <a 
                                                                    data-fancybox="gallery" 
                                                                    data-download-src={row.download_url} 
                                                                    // Logic: If it's a ppt/pptx, wrap it in the Office Viewer URL
                                                                    href={
                                                                        ['ppt', 'pptx'].includes(row.file_type) 
                                                                        ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(row.view_url)}` 
                                                                        : row.view_url
                                                                    }
                                                                    // Force 'pdf' or 'iframe' for documents to ensure Fancybox doesn't try to render them as images
                                                                    data-type={['ppt', 'pptx', 'doc', 'docx'].includes(row.file_type) ? "pdf" : ""} 
                                                                    className="fancybox image gallery-item m-0" 
                                                                    title="View"
                                                                >
                                                                    <button className="btn btn-sm btn-behance p-1" title="View Media">
                                                                        <i className="mdi mdi-eye"></i>
                                                                    </button>
                                                                </a>
                                                            </Fancybox>
                                                            </div>
                                                        )}

                                                        <div>
                                                            <button 
                                                                type="button"
                                                                className={`btn btn-sm btn-inverse-info ms-2 p-1`} 
                                                                title={`Edit ${row.title}`}
                                                                onClick={(e) => toggleModal(e, 'resource_edit', true, row)}
                                                            >
                                                                <i className="mdi mdi-pencil"></i>
                                                            </button>
                                                        </div>

                                                        <div>
                                                            {row.download_url && (
                                                                <button 
                                                                    type="button"
                                                                    className={`btn btn-sm btn-facebook ms-2 p-1`} 
                                                                    title={`Download ${row.title}`}
                                                                    onClick={(e) => handleDownload(e, row.download_url)}
                                                                    // disabled={!url}
                                                                >
                                                                    <i className="mdi mdi-download"></i>
                                                                </button>
                                                            )}
                                                        </div>

                                                        {props.userRoleIds.includes(2) && (
                                                            <div>
                                                                <button 
                                                                    type="button"
                                                                    className={`btn btn-sm btn-danger ms-2 p-1`} 
                                                                    title={`Delete ${row.title}`}
                                                                    onClick={(e) => deleteResource(e, row.id)}
                                                                >
                                                                    <i className="mdi mdi-trash-can"></i>
                                                                </button>
                                                            </div>
                                                        )}
                                                        
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    )
                                    : 
                                    <tr>
                                        {/* <td colSpan={props.userRoleIds.includes(2) ? 13 : 11}> */}
                                        <td colSpan="20">
                                        <div className="no-fellow text-center mt-4">
                                            {/* {emptyImage && <img src={emptyImage} alt="No Data" />} */}
                                            <p className="pt-4 head">{"No Record Found!"}</p>
                                        </div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </InfiniteListWrapper>
        </div>

        {/* Modal */}
        <Modal dialogClassName="z-index-99999" show={modalToggle.resource_new} onHide={(e) => toggleModal(e, 'resource_new', false)} size="lg" className="mod-custom team-detail" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Add Resources</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="scroll-wrap">
                    <div className="scroll-div">
                        <Form className="filter-form">
                            <fieldset>
                                {/* <legend>{form.label}</legend> */}
                                <div className="row m-0">
                                    {/* Program */}
                                    <Col xs={12} md="6" className="p-1 mb-4">
                                        <FilterInput
                                            formType="resource_new"
                                            field={{
                                                label: "IFSPD Program",
                                                type: "select",
                                                required: true,
                                                placeholderData: ["Select Program"],
                                                name: "program_id"
                                            }}
                                            data={newResourceData}
                                            selectOptions={{program_id: {
                                                        value: "id",
                                                        label: "label",
                                                        data: dropdownOpt?.program_id}}}
                                            _updateInput= {(form_type, data_type, input_type, field, value) => updateInput(form_type, field, value)}
                                        />
                                    </Col>

                                    {/* Level/Year */}
                                    {(newResourceData?.program_id
                                        && getProgramResourceConfig(newResourceData.program_id?.id).showLevel)
                                        && (
                                            <Col xs={12} md="6" className="p-1 mb-4">
                                                <FilterInput
                                                    formType="resource_new"
                                                    field={{
                                                        label: "Level/Year",
                                                        type: "select",
                                                        required: true,
                                                        placeholderData: ["Select Level/Year"],
                                                        name: "level_id"
                                                    }}
                                                    data={newResourceData}
                                                    selectOptions={{level_id: {
                                                                value: "id",
                                                                label: "label",
                                                                data: dropdownOpt?.[getProgramResourceConfig(newResourceData.program_id?.id).levelOptKey]}}}
                                                    _updateInput= {(form_type, data_type, input_type, field, value) => updateInput(form_type, field, value)}
                                                />
                                            </Col>
                                        )
                                    }

                                    {/* Session */}
                                    {newResourceData?.program_id && (
                                        <Col xs={12} md="6" className="p-1 mb-4">
                                            <FilterInput
                                                formType="resource_new"
                                                field={{
                                                    label: "Council",
                                                    type: "select",
                                                    required: true,
                                                    placeholderData: ["Select Council"],
                                                    name: "session_id"
                                                }}
                                                data={newResourceData}
                                                selectOptions={{session_id: (() => {
                                                            const cfg = getProgramResourceConfig(newResourceData.program_id?.id);
                                                            const sessionOptions = dropdownOpt?.[cfg.sessionOptKey];
                                                            return {
                                                                value: "id",
                                                                label: "label",
                                                                data: cfg.sessionMode === "full"
                                                                    ? sessionOptions
                                                                    : [sessionOptions?.[0]]
                                                            };
                                                        })()}}
                                                _updateInput= {(form_type, data_type, input_type, field, value) => updateInput(form_type, field, value)}
                                            />
                                        </Col>
                                    )}
                                            
                                    {/* Resources */}
                                    <Col xs={12} md="6" className="p-1 mb-4">
                                        <div className="mb-0">
                                            <label className="form-label">Resources (12 Files/ 300MB) (image, pdf, pptx, ppt, mp4)<em className="required">*</em></label>
                                            <div className="btn btn-light btn-outline-behance btn-sm w-100 position-relative">
                                                <span>Upload pdf/video/ppt/image</span>
                                                <input
                                                    type="file"
                                                    className="logo-input"
                                                    name="profile_log"
                                                    onChange={(e) => previewResources(e)}
                                                    accept="image/*,application/pdf,video/mp4,.ppt,.pptx"
                                                    multiple
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    {/* Resources Details */}
                                    {newResourceData.files.length > 0 && (
                                    // <div className="card">
                                        <div className="table-responsive sticky-table card p-0 h-min-auto">
                                                <table className="table">
                                                    <thead>
                                                        <tr>
                                                            <th className="w-50p">SL No.</th>
                                                            <th>Files<em className="required">*</em> ({newResourceData.files.length})/Title<em className="required">*</em>/Priority</th>
                                                            <th>Introduction</th>
                                                            {/* <th>Title<em className="required">*</em></th> */}
                                                            <th>Description</th>
                                                            <th className="w-110p">Size<br/>({(newResourceData.file_size / (1024 * 1024)).toFixed(2) + " MB"})</th>
                                                            {totalUploadedFiles !== pendingFilesCount
                                                                ? <th>Status</th>
                                                                : <th>Remove</th>     
                                                            }
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {newResourceData.files.map((r_file, r_index) => (
                                                            <tr key={`res_${r_index}`}>
                                                                <td className="align-baseline">
                                                                    {r_index + 1}
                                                                </td>
                                                                <td className="w-125p">
                                                                    <div>
                                                                        {r_file.docType === "video"
                                                                        ?
                                                                            // <video controls width="100" height="100px">
                                                                            // <source src={r_file.src} type="video/mp4" />
                                                                            // </video>
                                                                            <img src={r_file.thumbnail} className="w-100 h-100 rounded-2 img-thumbnail" width="100" height="100" alt="Resources"/>
                                                                        :
                                                                            r_file.docType === "pdf"
                                                                            ?
                                                                                // <iframe src={r_file.src} width="100" height="100" title={`res_pdf_${r_index}`}></iframe>
                                                                                <img src={r_file.thumbnail} className="w-100 h-100 rounded-2 img-thumbnail" width="100" height="100" alt="Resources"/>
                                                                            :
                                                                                r_file.docType === "ppt"
                                                                                ?
                                                                                    <iframe 
                                                                                    src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(r_file.src)}`}
                                                                                    title={`res_pdf_${r_index}`}
                                                                                    width="100" 
                                                                                    height="100">
                                                                                    </iframe>
                                                                                :
                                                                                    r_file.docType === "image"
                                                                                    ?
                                                                                        <img src={r_file.src} className="w-100 h-100 rounded-2 img-thumbnail" width="100" height="100" alt="Resources"/>
                                                                                    : ""

                                                                        }
                                                                        <p className="mb-0">{r_file.name}</p>
                                                                    </div>
                                                                    <div className="mt-1">
                                                                        <textarea
                                                                            className="form-control w-200p"
                                                                            name="title"
                                                                            rows="7"
                                                                            value={r_file.title}
                                                                            placeholder="Title"
                                                                            onChange={(e) => updateInput('resource_new','title', e.target.value, r_index)}
                                                                            maxLength={200}
                                                                        />
                                                                    </div>
                                                                    <div className="mt-1">
                                                                        <input
                                                                            className="form-control"
                                                                            type="number"
                                                                            name="display_order"
                                                                            value={r_file.display_order}
                                                                            placeholder="Display Order"
                                                                            onChange={(e) => updateInput('resource_new','display_order', e.target.value, r_index)}
                                                                            max={1000}
                                                                            min={1}
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className="align-baseline">
                                                                    {/* <textarea
                                                                        className="form-control"
                                                                        name="introduction"
                                                                        rows="5"
                                                                        value={r_file.introduction}
                                                                        placeholder="Introduction"
                                                                        onChange={(e) => updateInput('resource_new','introduction', e.target.value, r_index)}
                                                                        maxLength={1000}
                                                                    /> */}
                                                                    <CKEditor
                                                                        editor={ ClassicEditor }
                                                                        data={r_file.introduction}
                                                                        onChange={ ( event, editor ) => {
                                                                            // const data = editor.getData();
                                                                            // console.log( { event, editor, data } );
                                                                            updateInput('resource_new','introduction', editor.getData(), r_index)
                                                                        }}
                                                                        onReady={(editor) => {
                                                                            // You can store the "editor" and use when it is needed.
                                                                            // console.log("Editor is ready to use!", editor);
                                                                            editor.editing.view.change((writer) => {
                                                                                writer.setStyle(
                                                                                    "height",
                                                                                    // `${field.height ? field.height : '200'}px`,
                                                                                    '300px',
                                                                                    editor.editing.view.document.getRoot()
                                                                                );
                                                                            });
                                                                        }}
                                                                        config={ {
                                                                            toolbar: [ 'bold', '|', 'italic', '|', 'bulletedList', '|', 'numberedList'],
                                                                        }}
                                                                    />
                                                                </td>
                                                                {/* <td className="align-baseline">
                                                                    <textarea
                                                                        className="form-control w-200p"
                                                                        name="title"
                                                                        rows="7"
                                                                        value={r_file.title}
                                                                        placeholder="Title"
                                                                        onChange={(e) => updateInput('resource_new','title', e.target.value, r_index)}
                                                                        maxLength={200}
                                                                    />
                                                                </td> */}
                                                                <td className="align-baseline">
                                                                    {/* <textarea
                                                                        className="form-control"
                                                                        name="description"
                                                                        rows="5"
                                                                        value={r_file.description}
                                                                        placeholder="Description"
                                                                        onChange={(e) => updateInput('resource_new','description', e.target.value, r_index)}
                                                                        maxLength={1000}
                                                                    /> */}
                                                                    <CKEditor
                                                                        editor={ ClassicEditor }
                                                                        data={r_file.description}
                                                                        onChange={ ( event, editor ) => {
                                                                            // const data = editor.getData();
                                                                            // console.log( { event, editor, data } );
                                                                            updateInput('resource_new','description', editor.getData(), r_index)
                                                                        }}
                                                                        onReady={(editor) => {
                                                                            // You can store the "editor" and use when it is needed.
                                                                            // console.log("Editor is ready to use!", editor);
                                                                            editor.editing.view.change((writer) => {
                                                                                writer.setStyle(
                                                                                    "height",
                                                                                    // `${field.height ? field.height : '200'}px`,
                                                                                    '300px',
                                                                                    editor.editing.view.document.getRoot()
                                                                                );
                                                                            });
                                                                        }}
                                                                        config={ {
                                                                            toolbar: [ 'bold', '|', 'italic', '|', 'bulletedList', '|', 'numberedList'],
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td className="align-baseline">
                                                                    {(r_file.size / (1024 * 1024)).toFixed(2) + " MB"}
                                                                </td>
                                                                <td className="align-baseline w-50p">
                                                                    {r_file.status !== 'pending' 
                                                                        ?
                                                                        <>
                                                                        {/* 1. Show Status Badge */}
                                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                                            <Badge 
                                                                                bg={
                                                                                    r_file.status === 'completed' ? 'success' : 
                                                                                    r_file.status === 'failed' ? 'danger' : 
                                                                                    r_file.status === 'uploading' ? 'primary' : 'secondary'
                                                                                }
                                                                                className="text-capitalize"
                                                                            >
                                                                                {r_file.status === 'uploading' && (
                                                                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                                                )}
                                                                                {r_file.status}
                                                                            </Badge>
                                                                            
                                                                            {/* Show percentage text only when relevant */}
                                                                            {(r_file.status === 'uploading' || r_file.status === 'completed') && (
                                                                                <small className="text-muted fw-bold">{r_file.progress}%</small>
                                                                            )}
                                                                        </div>

                                                                        {/* 2. Show Progress Bar */}
                                                                        <ProgressBar 
                                                                            animated={r_file.status === 'uploading'} 
                                                                            variant={
                                                                                r_file.status === 'completed' ? 'success' : 
                                                                                r_file.status === 'failed' ? 'danger' : 'primary'
                                                                            } 
                                                                            now={r_file.progress} 
                                                                            style={{ height: '8px' }}
                                                                            className="shadow-sm"
                                                                        />

                                                                        {/* 3. Show Error Hint (Optional) */}
                                                                        {r_file.status === 'failed' && (
                                                                            <div className="text-danger mt-1" style={{ fontSize: '11px' }}>
                                                                                <i className="fa fa-exclamation-circle me-1"></i> Upload failed. Try again.
                                                                            </div>
                                                                        )}
                                                                        </>
                                                                        :
                                                                        <button
                                                                            className="btn btn-outline-danger p-2"
                                                                            onClick={(e) => removeResource(e, r_index)}
                                                                        ><i className="mdi mdi-trash-can-outline"></i></button>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                        </div>
                                    // </div>
                                    )}
                                </div>
                            </fieldset>

                            {/* Error */}
                            {(errorData.resource_new && errorData.resource_new.length > 0) && (
                                <div className="col-md-12 col-12 col-sm-12 ps-2 pe-2 ps-sm-3 pe-sm-3">
                                    <div className="alert alert-danger mt-4" role="alert">
                                        <p>Please enter/modify following Data:</p>
                                        <ol>
                                        {errorData.resource_new.map((error, error_index) => (
                                            <li key={`error_${error_index}`}>{error}</li>
                                        ))}
                                        </ol>
                                    </div>
                                </div>
                            )}

                            {/* Master Progress Row */}
                            {totalUploadedFiles !== pendingFilesCount
                            && (
                                <div className="row mb-4 mt-4">
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm bg-light">
                                            <div className="card-body p-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="fw-bold">
                                                        {isUploadingAny ? 'Processing Uploads...' : 'Upload Summary'}
                                                    </span>
                                                    <span className="badge bg-primary">
                                                        {completedFiles} / {totalUploadedFiles} Done
                                                    </span>
                                                </div>
                                                <ProgressBar 
                                                    now={combinedProgress} 
                                                    variant={combinedProgress === 100 ? "success" : "primary"}
                                                    animated={isUploadingAny}
                                                    style={{ height: '10px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="text-center mb-2 mt-3 justify-content-center filter-excel">
                                <div className="d-inline-flex">
                                {(newResourceData.files.some(file => file.status === 'failed')
                                    && !isProcess.resource_new)
                                    ?
                                    <button 
                                        onClick={retryFailedUploads}
                                        className="btn btn-warning btn-sm d-flex align-items-center"
                                    >
                                        <i className="mdi mdi-refresh me-2"></i> 
                                        Retry Failed Uploads ({newResourceData.files.filter(f => f.status === 'failed').length})
                                    </button>
                                    :
                                    !isProcess.resource_new
                                    ?
                                        <button className="btn btn-primary bg-color-blue btn-sm" type="submit" onClick={(e) => uploadAllResources(e)}><span className="fs-6">Submit</span></button>
                                    :
                                        <button className="btn btn-primary bg-color-blue btn-sm d-flex" disabled type="submit"><LoaderMedium size={18} /> &nbsp;<span className="fs-6">Submitting...</span></button>
                                }
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="team-detail">
                <button className="btn btn-secondary" onClick={(e) => toggleModal(e, 'resource_new', false)}>Close</button>
            </Modal.Footer>
        </Modal>

        {/* Edit Details */}
        <ModalMaster
            isShow={modalToggle.resource_edit}
            // loader={modalLoader.resource_edit}
            loader={false}
            _toggleModal={(e, state) => toggleModal(e, 'resource_edit', state)}
            // modKey={activeEditForm}
            modKey = "resource_edit"
            formType={'resource_edit'}
            data={formEditData}
            // title={masterTitle[activeEditForm]}
            title={"Update Details"}
            _errorData={{...errorData}}
            _isProcess={{...isProcess}}
            // _isBtnDisabled={{...isBtnDisabled}}
            _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(form_type, field, value)}
            _submitForm={(e, form_type) => submitForm(e, form_type)}
            _formData={[...masterFieldData]}
            selectOptions={{}}
        />
        </>
    );
}

export default GloscoResourcesManage;